import React from "react";
import { useQuery } from "react-query";
import { useIntl } from "react-intl";
import {
  Autocomplete,
  CircularProgress,
  TextField,
  Chip,
  TextFieldProps,
} from "@mui/material";
import {
  ListRequestOfBaseFilter,
  ListResponseOfState,
  State,
} from "services/KnowfullyAdminClient";
import useGlobalContext from "hooks/globalData";
import { ALL_APPROVED_ID } from "helpers/skuQualificationHelpers";

type SelectStateMultipleProps = {
  value: string[];
  onChange: (ids: string[]) => void;
  required?: boolean;
  label?: string;
  disabled?: boolean;
  textFieldProps?: Partial<TextFieldProps>;
};

const SelectStateMultiple = ({
  value,
  onChange,
  required,
  label,
  disabled,
  textFieldProps,
}: SelectStateMultipleProps) => {
  const { formatMessage: t } = useIntl();
  const { stateService } = useGlobalContext();

  const fetchedStates = useQuery<ListResponseOfState>(
    ["state"],
    () =>
      stateService.getStates(
        ListRequestOfBaseFilter.fromJS({ sortField: "stateName" })
      ),
    {
      onSuccess: (data) => {
        if (data?.data) {
          for (const state of data.data) {
            state.stateName = `${state.stateName!} (${state.name!})`;
          }

          data.data.unshift(
            new State({
              id: ALL_APPROVED_ID,
              stateName: t({ id: "allApprovedStatesLabel" }),
            })
          );
        }
      },
    }
  );

  const states = fetchedStates.data?.data || [];

  const selectedValues = Array.isArray(value)
    ? states.filter((s) => value.includes(s.id!))
    : [];

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: State
  ) => {
    if (option.id === ALL_APPROVED_ID) {
      return (
        <li {...props}>
          <b>{option.stateName}</b>
        </li>
      );
    }

    return <li {...props}>{option.stateName}</li>;
  };

  return (
    <Autocomplete
      multiple
      autoComplete
      disableCloseOnSelect
      limitTags={3}
      loading={fetchedStates.isLoading}
      disabled={disabled || fetchedStates.isLoading}
      options={states}
      value={selectedValues}
      renderOption={renderOption}
      getOptionLabel={(option) => option.stateName || ""}
      onChange={(_event, newValue) => {
        if (newValue.some((v) => v.id === ALL_APPROVED_ID)) {
          onChange([ALL_APPROVED_ID]);
        } else {
          onChange(newValue.map((v) => v.id!).filter(Boolean));
        }
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            size="small"
            label={option.stateName}
            {...getTagProps({ index })}
            key={option.id}
          />
        ))
      }
      renderInput={(input) => (
        <TextField
          variant="standard"
          {...textFieldProps}
          {...input}
          required={required}
          label={label || t({ id: "locationFormStateLabel" })}
          InputProps={{
            ...textFieldProps?.InputProps,
            ...input.InputProps,
            endAdornment: (
              <>
                {fetchedStates.isLoading ? <CircularProgress size={16} /> : null}
                {input.InputProps.endAdornment}
              </>
            ),
          }}
          fullWidth
        />
      )}
    />
  );
};

export default SelectStateMultiple;
